import React, { useEffect, useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ZAxis,
} from "recharts";
import { useStockTraderStatsQuery } from "hooks/queries/useStockDetailQuery";
import NPS from "components/NPS";

const Container = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 1rem;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    padding: 1rem 0.5rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #f1f1f1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 1rem;
`;

const CardHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #f1f1f1;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 1rem;
`;

const StockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`;

const StockInfo = styled.div`
  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  p {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }
`;

const StockPrice = styled.div`
  text-align: right;

  .price {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
  }

  .change {
    font-size: 0.875rem;
    color: ${(props) => (props.$isPositive ? "#10B981" : "#EF4444")};
  }
`;

const ChartWrapper = styled.div`
  height: 400px;
  width: 100%;
`;

const TraderStats = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const StatBadge = styled.div`
  background: ${(props) => (props.$type === "buy" ? "#DEF7EC" : "#FDE2E1")};
  color: ${(props) => (props.$type === "buy" ? "#059669" : "#DC2626")};
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .count {
    font-size: 1.125rem;
    font-weight: 600;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  th,
  td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #f1f1f1;
  }

  th {
    font-weight: 500;
    color: #6b7280;
    background: #f9fafb;
  }

  td {
    color: #111827;
  }

  tr:hover td {
    background: #f9fafb;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.$type) {
      case "buy":
        return "#DEF7EC";
      case "sell":
        return "#FDE2E1";
      case "hold":
        return "#F3F4F6";
      default:
        return "#F3F4F6";
    }
  }};
  color: ${(props) => {
    switch (props.$type) {
      case "buy":
        return "#059669";
      case "sell":
        return "#DC2626";
      case "hold":
        return "#6B7280";
      default:
        return "#6B7280";
    }
  }};
`;

const CustomTooltip = styled.div`
  background: white;
  border: 1px solid #f1f1f1;
  border-radius: 0.5rem;
  padding: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .trader {
    font-weight: 500;
    margin-bottom: 0.25rem;
  }

  .price {
    color: #6b7280;
    font-size: 0.875rem;
  }
`;

const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(5px); }
  50% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
  100% { transform: translateX(0); }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  width: 200px;
  &:focus {
    outline: none;
    border-color: #2563eb;
  }
`;

const SearchButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: ${(props) => (props.$isValid ? "#2563eb" : "#e5e7eb")};
  color: ${(props) => (props.$isValid ? "white" : "#6b7280")};
  border: none;
  cursor: ${(props) => (props.$isValid ? "pointer" : "not-allowed")};
  animation: ${(props) => (props.$shake ? shakeAnimation : "none")} 0.5s;

  &:hover {
    background: ${(props) => (props.$isValid ? "#1d4ed8" : "#e5e7eb")};
  }
`;

const ErrorMessage = styled.span`
  color: #dc2626;
  font-size: 0.875rem;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.25rem;
  color: #6b7280;
`;

const ErrorWrapper = styled.div`
  text-align: center;
  padding: 2rem;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 0.5rem;
  margin: 1rem 0;
`;

const StockPage = () => {
  const [stockCode, setStockCode] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [shake, setShake] = useState(false);
  const [showError, setShowError] = useState(false);

  const {
    data: stockData,
    isLoading,
    isError,
  } = useStockTraderStatsQuery(stockCode);

  console.log("Stock Data:", stockData);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    if (code) {
      setStockCode(code);
      setSearchInput(code);
    }
  }, []);

  const isValidCode = (code) => {
    return /^[a-zA-Z0-9]{6}$/.test(code);
  };

  const handleSearch = () => {
    if (isValidCode(searchInput)) {
      setStockCode(searchInput);
      const newUrl = `${window.location.pathname}?code=${searchInput}`;
      window.history.pushState({ path: newUrl }, "", newUrl);
      setShowError(false);
    } else {
      setShake(true);
      setShowError(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const calculateTradingStats = (tradesData) => {
    if (!tradesData) return { buyTotal: 0, sellTotal: 0, holdTotal: 0 };

    const clearStats = tradesData.clear?.cnt_bhs || {};
    const unclearStats = tradesData.unclear?.cnt_bhs || {};

    let buyTotal = 0;
    let sellTotal = 0;
    let holdTotal = 0;

    Object.values(clearStats).forEach(({ buy, sell, hold }) => {
      buyTotal += buy || 0;
      sellTotal += sell || 0;
      holdTotal += hold || 0;
    });

    Object.values(unclearStats).forEach(({ buy, sell, hold }) => {
      buyTotal += buy || 0;
      sellTotal += sell || 0;
      holdTotal += hold || 0;
    });

    return { buyTotal, sellTotal, holdTotal };
  };

  const scatterData = useMemo(() => {
    if (!stockData?.holder?.trader) return [];
    return stockData.holder.trader.map((trader) => ({
      x: trader.avg_prc,
      y: new Date(trader.last_update).getTime(),
      z: trader.weight,
      trader: trader.nick,
      league: trader.league,
      rank: trader.rank,
    }));
  }, [stockData]);

  const CustomScatterTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    return (
      <CustomTooltip>
        <div className="trader">{data.trader}</div>
        <div className="price">평단가: ₩{data.x.toLocaleString()}</div>
        <div className="price">비중: {data.z}%</div>
        <div className="price">
          {data.league} 리그 {data.rank}위
        </div>
      </CustomTooltip>
    );
  };

  if (isLoading)
    return <LoadingWrapper>데이터를 불러오는 중...</LoadingWrapper>;
  if (isError)
    return (
      <ErrorWrapper>
        데이터를 불러오는 데 실패했습니다. 다시 시도해 주세요.
      </ErrorWrapper>
    );
  if (!stockData && stockCode)
    return <ErrorWrapper>해당 종목 정보를 찾을 수 없습니다.</ErrorWrapper>;

  // 데이터 구조 유효성 검사
  if (!stockData?.info || !stockData?.holder || !stockData?.trades) {
    console.error("Invalid data structure:", stockData);
    return <ErrorWrapper>데이터 구조가 올바르지 않습니다.</ErrorWrapper>;
  }

  const tradingStats = calculateTradingStats(stockData?.trades);
  const sortedTraders =
    stockData?.holder?.trader?.sort((a, b) => b.avg_prc - a.avg_prc) || [];

  const dailyTradingStats = stockData?.trades?.clear?.cnt_bhs
    ? Object.entries(stockData.trades.clear.cnt_bhs)
        .map(([date, stats]) => ({
          date,
          ...stats,
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date))
    : [];

  return (
    <Container>
      <SearchContainer>
        <SearchInput
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
          onKeyPress={handleKeyPress}
          placeholder="종목 코드 입력"
          maxLength={6}
        />
        <SearchButton
          onClick={handleSearch}
          $isValid={isValidCode(searchInput)}
          $shake={shake}
        >
          검색
        </SearchButton>
        {showError && (
          <ErrorMessage>
            종목 코드를 확인해주세요 (6자리 영문/숫자)
          </ErrorMessage>
        )}
      </SearchContainer>

      {stockData?.info?.name ? (
        <>
          <Card>
            <CardContent>
              <StockHeader>
                <StockInfo>
                  <h1>{stockData.info.name}</h1>
                  <p>{stockData.info.code}</p>
                </StockInfo>
                <StockPrice>
                  <div className="price">
                    ₩{stockData.holder.stats.prc.avg.toLocaleString()}
                  </div>
                  <div className="change">평균 매수가</div>
                </StockPrice>
              </StockHeader>

              <TraderStats>
                <StatBadge $type="buy">
                  <span>누적 매수자</span>
                  <span className="count">{tradingStats.buyTotal}명</span>
                </StatBadge>
                <StatBadge $type="sell">
                  <span>누적 매도자</span>
                  <span className="count">{tradingStats.sellTotal}명</span>
                </StatBadge>
                <StatBadge $type="hold">
                  <span>현재 보유자</span>
                  <span className="count">{tradingStats.holdTotal}명</span>
                </StatBadge>
              </TraderStats>

              <ChartWrapper>
                <ResponsiveContainer>
                  <ScatterChart
                    margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  >
                    <YAxis
                      type="number"
                      dataKey="x"
                      name="평단가"
                      domain={["auto", "auto"]}
                      tickFormatter={(value) => `₩${value.toLocaleString()}`}
                    />
                    <XAxis
                      type="number"
                      dataKey="y"
                      name="날짜"
                      domain={["auto", "auto"]}
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString()
                      }
                    />
                    <ZAxis type="number" dataKey="z" range={[50, 400]} />
                    <Tooltip content={<CustomScatterTooltip />} />
                    <Scatter data={scatterData} fill="#2563EB" shape="cross" />
                  </ScatterChart>
                </ResponsiveContainer>
              </ChartWrapper>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>보유 트레이더 (평단가 순)</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <thead>
                  <tr>
                    <th>닉네임</th>
                    <th>리그</th>
                    <th>순위</th>
                    <th>예상 손익</th>
                    <th>비중</th>
                    <th>평균단가</th>
                    <th>최근 매수일</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTraders.map((trader) => (
                    <tr key={trader.cuid}>
                      <td>{trader.nick}</td>
                      <td>{trader.league}</td>
                      <td>{trader.rank}위</td>
                      <td>₩{trader.est_pnl.toLocaleString()}</td>
                      <td>{trader.weight}%</td>
                      <td>₩{trader.avg_prc.toLocaleString()}</td>
                      <td>{trader.str_last_buy || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>일별 거래 현황</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <thead>
                  <tr>
                    <th>날짜</th>
                    <th>매수</th>
                    <th>보유</th>
                    <th>매도</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyTradingStats.map((stats) => (
                    <tr key={stats.date}>
                      <td>{stats.date}</td>
                      <td>
                        {stats.buy > 0 && (
                          <Badge $type="buy">{stats.buy}명</Badge>
                        )}
                      </td>
                      <td>
                        {stats.hold > 0 && (
                          <Badge $type="hold">{stats.hold}명</Badge>
                        )}
                      </td>
                      <td>
                        {stats.sell > 0 && (
                          <Badge $type="sell">{stats.sell}명</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}

      <NPS />
    </Container>
  );
};

export default StockPage;
